import React, {useState} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import {Check, X, HelpCircle, AlertTriangle, ChevronDown, ChevronRight, LoaderCircle, CircleAlert} from "lucide-react";
import {getContrastColors} from "../../UIElements";

function isBlocked(session) {
    const a = session.llmanalysis
    return a && (a.error === "BLOCKED" || a.blocked === true)
}

function computeStats(sessions) {
    const total = sessions.length
    const analyzed = sessions.filter(s => s.llmanalysis && !isBlocked(s))
    const blocked = sessions.filter(s => isBlocked(s)).length
    const notAnalyzed = total - analyzed.length - blocked

    let successful = 0, failed = 0, uncertain = 0, supportGiven = 0, requestSuggested = 0

    for (const s of analyzed) {
        const a = s.llmanalysis
        if (a.conversation_success === true) successful++
        else if (a.conversation_success === false) failed++
        else uncertain++

        if (a.support_given) supportGiven++
        if (a.support_request_suggested) requestSuggested++
    }

    const successRate = analyzed.length > 0
        ? Math.round((successful / analyzed.length) * 100)
        : null

    return {
        total, notAnalyzed, analyzed: analyzed.length, blocked,
        successful, failed, uncertain,
        supportGiven, requestSuggested,
        successRate,
    }
}

// Chooses the empty-state message based on why the set is empty:
// loading → "Loading"; no conversations in the date range → "in selected period";
// conversations exist but filters excluded all → "match the current filters";
// otherwise the section-specific fallback (e.g. "none analyzed yet" / "no topic data").
function StatEmpty({periodSessionCount, totalSessions, isLoading, fallback}) {
    let message = fallback
    if (isLoading) {
        message = <FormattedMessage id="analysis.loading" defaultMessage="Loading..." />
    } else if (periodSessionCount === 0) {
        message = <FormattedMessage id="analysis.stats.noConversationsInPeriod" defaultMessage="No conversations in the selected period" />
    } else if (totalSessions === 0) {
        message = <FormattedMessage id="analysis.stats.noMatchingConversations" defaultMessage="No conversations match the current filters" />
    }
    return <div className="stat-empty">{message}</div>
}

function StatRow({icon, count, label, percentage, colorClass = "", className = "", isActive, onClick}) {
    return <tr
        className={`stat-row ${colorClass} ${className} ${isActive ? "active" : ""}`}
        onClick={onClick}
    >
        <td className="stat-row-label">
            <div className={"flex"}>
                {icon && <span className="icon">{icon}</span>}
                {label}
            </div>
        </td>
        <td className="stat-row-count">{count}</td>
        <td className="stat-row-pct">{percentage != null ? `(${percentage}%)` : ""}</td>
    </tr>
}

function ThemeBarChart({themeGroupedCounts, activeTopics, onThemeClick, onTopicClick, coverageSubtitle}) {
    const [expandedThemes, setExpandedThemes] = useState(new Set())
    const maxCount = themeGroupedCounts[0]?.totalCount || 1

    const toggleExpanded = (theme, e) => {
        e.stopPropagation()
        setExpandedThemes(prev => {
            const next = new Set(prev)
            if (next.has(theme)) next.delete(theme)
            else next.add(theme)
            return next
        })
    }

    return <div className="theme-bar-chart stat-group">
        <div className="stat-group-heading">
            <FormattedMessage id="analysis.stats.conversationTopics" defaultMessage="Conversation Topics" />
        </div>
        {coverageSubtitle}
        <div className={"theme-bar-container"}>
            {themeGroupedCounts.map(themeData => {
                const isExpanded = expandedThemes.has(themeData.theme)
                const widthPercent = Math.round((themeData.totalCount / maxCount) * 100)
                const topicCodes = themeData.topics.map(t => t.code)
                const allTopicsActive = themeData.topics.every(t => activeTopics?.includes(t.code))
                const someTopicsActive = themeData.topics.some(t => activeTopics?.includes(t.code))

                const {textColor} = getContrastColors(themeData.color)

                return <div key={themeData.theme} className="theme-bar-group">
                    <div className={`theme-bar-wrap ${allTopicsActive ? "active" : someTopicsActive ? "partial" : ""}`}>
                        <div
                            className="expand-icon"
                            onClick={(e) => toggleExpanded(themeData.theme, e)}
                        >
                            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </div>
                        <div
                            className="bar-container"
                            onClick={() => onThemeClick(themeData.theme, topicCodes)}
                        >
                            <div
                                className="bar"
                                style={{width: `${widthPercent}%`, backgroundColor: themeData.color}}
                            />
                            <span className="title">
                                {themeData.themeLabel}
                            </span>
                            <div
                                className="bar-overlay"
                                style={{width: `${widthPercent}%`, color: textColor}}
                            >
                                <span className="title">
                                    {themeData.themeLabel}
                                </span>
                            </div>
                        </div>
                        <div className="label">
                            <b>{themeData.totalCount}</b>
                        </div>
                    </div>

                    {isExpanded && <div className="theme-topics-expanded">
                        {themeData.topics.map(topic => {
                            const topicWidthPercent = Math.round((topic.count / maxCount) * 100)
                            const isActive = !allTopicsActive && activeTopics?.includes(topic.code)
                            return <div
                                key={topic.code}
                                className={`topic-bar-wrap ${isActive ? "active" : ""}`}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    onTopicClick(topic.code, topicCodes)
                                }}
                            >
                                <div className="bar-container">
                                    <span className="title">{topic.label}</span>
                                    <div
                                        className="bar"
                                        style={{
                                            width: `${topicWidthPercent}%`,
                                            backgroundColor: themeData.color,
                                        }}
                                    />
                                    <div
                                        className="bar-overlay"
                                        style={{width: `${topicWidthPercent}%`, color: textColor}}
                                    >
                                        <span className="title">{topic.label}</span>
                                    </div>
                                </div>
                                <div className="label">
                                    <b>{topic.count}</b>
                                </div>
                            </div>
                        })}
                    </div>}
                </div>
            })}
        </div>
    </div>
}

export default function StatsHeader({
    sessions,
    supportNeedsCount,
    escalationPendingCount,
    activeFilter,
    onFilterClick,
    themeGroupedCounts,
    activeTopics,
    onTopicClick,
    onThemeClick,
    totalSessions,
    analyzedCount,
    unanalyzedCount,
    onAnalyzeAll,
    isAnalyzing,
    analyzingRemaining,
    periodSessionCount,
    isLoading,
}) {
    const intl = useIntl()
    const stats = computeStats(sessions)
    const coveragePercent = totalSessions > 0 ? Math.round((analyzedCount / totalSessions) * 100) : 0
    const allAnalyzed = unanalyzedCount === 0
    // Keep the stat tables visible while a stat filter is active so the clicked row
    // stays highlighted (and toggleable), even when it narrows the set to zero matches.
    const noneAnalyzed = analyzedCount === 0 && activeFilter.size === 0
    const showSubtitle = !allAnalyzed && !noneAnalyzed

    const coverageSubtitle = showSubtitle && <div className="stat-group-subtitle">
        <CircleAlert size={14} />
        <FormattedMessage
            id="analysis.stats.missingAnalysis"
            defaultMessage="Missing analysis of {count} {count, plural, one {conversation} other {conversations}}"
            values={{count: unanalyzedCount}}
        />
    </div>

    return <div className="stats-header">
        {!allAnalyzed && <div className="coverage-banner">
            <div className="coverage-info">
                <span className="coverage-text">
                    <FormattedMessage
                        id="analysis.stats.coverage"
                        defaultMessage="{analyzed} of {total} {total, plural, one {conversation} other {conversations}} analyzed"
                        values={{analyzed: analyzedCount, total: totalSessions}}
                    />
                </span>
                <div className="coverage-bar">
                    <div className="coverage-fill" style={{width: `${coveragePercent}%`}} />
                </div>
            </div>
            {isAnalyzing ? (
                <span className="analyzing-status">
                    <LoaderCircle size={14} className="spin" />
                    <FormattedMessage
                        id="analysis.stats.analyzing"
                        defaultMessage="Analyzing... ({remaining} remaining)"
                        values={{remaining: analyzingRemaining}}
                    />
                </span>
            ) : (
                <button className="analyze-btn" onClick={onAnalyzeAll}>
                    <FormattedMessage id="analysis.analyzeAll" defaultMessage="Analyze all" />
                </button>
            )}
        </div>}

        <div className="stats-groups">
            <div className="stat-group">
                <div className="stat-group-heading">
                    <FormattedMessage id="analysis.stats.botPerformance" defaultMessage="Bot Performance" />
                </div>
                {coverageSubtitle}
                {noneAnalyzed ? (
                    <StatEmpty
                        periodSessionCount={periodSessionCount}
                        totalSessions={totalSessions}
                        isLoading={isLoading}
                        fallback={<FormattedMessage id="analysis.stats.noAnalysis" defaultMessage="No conversations analyzed yet" />}
                    />
                ) : (
                    <table className="stat-table"><tbody>
                        <StatRow
                            icon={<Check size={14} />}
                            count={stats.successful}
                            label={intl.formatMessage({id: "analysis.successful", defaultMessage: "Successful"})}
                            percentage={stats.analyzed > 0 ? Math.round((stats.successful / stats.analyzed) * 100) : null}
                            colorClass="success"
                            isActive={activeFilter.has("successful")}
                            onClick={() => onFilterClick("successful")}
                        />
                        <StatRow
                            icon={<X size={14} />}
                            count={stats.failed}
                            label={intl.formatMessage({id: "analysis.failed", defaultMessage: "Failed"})}
                            percentage={stats.analyzed > 0 ? Math.round((stats.failed / stats.analyzed) * 100) : null}
                            colorClass="failed"
                            isActive={activeFilter.has("failed")}
                            onClick={() => onFilterClick("failed")}
                        />
                        <StatRow
                            icon={<HelpCircle size={14} />}
                            count={stats.uncertain}
                            label={intl.formatMessage({id: "analysis.uncertain", defaultMessage: "Uncertain"})}
                            percentage={stats.analyzed > 0 ? Math.round((stats.uncertain / stats.analyzed) * 100) : null}
                            colorClass="uncertain"
                            isActive={activeFilter.has("uncertain")}
                            onClick={() => onFilterClick("uncertain")}
                        />
                        {stats.blocked > 0 && (
                            <StatRow
                                icon={<AlertTriangle size={14} />}
                                count={stats.blocked}
                                label={intl.formatMessage({id: "analysis.blocked", defaultMessage: "Blocked"})}
                                percentage={stats.analyzed > 0 ? Math.round((stats.blocked / stats.analyzed) * 100) : null}
                                colorClass="blocked"
                                isActive={activeFilter.has("blocked")}
                                onClick={() => onFilterClick("blocked")}
                            />
                        )}
                    </tbody></table>
                )}
            </div>

            <div className="stat-group">
                <div className="stat-group-heading">
                    <FormattedMessage id="analysis.stats.botActions" defaultMessage="Bot Actions" />
                </div>
                {coverageSubtitle}
                {noneAnalyzed ? (
                    <StatEmpty
                        periodSessionCount={periodSessionCount}
                        totalSessions={totalSessions}
                        isLoading={isLoading}
                        fallback={<FormattedMessage id="analysis.stats.noAnalysis" defaultMessage="No conversations analyzed yet" />}
                    />
                ) : (
                    <table className="stat-table"><tbody>
                        <StatRow
                            count={stats.supportGiven}
                            label={intl.formatMessage({id: "analysis.supportGiven", defaultMessage: "Support given"})}
                            percentage={stats.analyzed > 0 ? Math.round((stats.supportGiven / stats.analyzed) * 100) : null}
                            isActive={activeFilter.has("supportGiven")}
                            onClick={() => onFilterClick("supportGiven")}
                        />
                        <StatRow
                            count={stats.requestSuggested}
                            label={intl.formatMessage({id: "analysis.supportRequestSuggested", defaultMessage: "Support request suggested"})}
                            percentage={stats.analyzed > 0 ? Math.round((stats.requestSuggested / stats.analyzed) * 100) : null}
                            isActive={activeFilter.has("requestSuggested")}
                            onClick={() => onFilterClick("requestSuggested")}
                        />
                        <StatRow
                            className="sub pending"
                            count={escalationPendingCount ?? 0}
                            label={intl.formatMessage({id: "analysis.funnel.notCreated", defaultMessage: "Not created"})}
                            percentage={null}
                            isActive={activeFilter.has("escalationPending")}
                            onClick={() => onFilterClick("escalationPending")}
                        />
                        <StatRow
                            count={supportNeedsCount ?? 0}
                            label={intl.formatMessage({id: "analysis.supportRequestCreated", defaultMessage: "Support request created"})}
                            percentage={stats.analyzed > 0 ? Math.round(((supportNeedsCount ?? 0) / stats.analyzed) * 100) : null}
                            isActive={activeFilter.has("needCreated")}
                            onClick={() => onFilterClick("needCreated")}
                        />
                    </tbody></table>
                )}
            </div>
            {themeGroupedCounts && themeGroupedCounts.length > 0 ? (
                <ThemeBarChart
                    themeGroupedCounts={themeGroupedCounts}
                    activeTopics={activeTopics}
                    onThemeClick={onThemeClick}
                    onTopicClick={onTopicClick}
                    coverageSubtitle={coverageSubtitle}
                />
            ) : (
                <div className="stat-group topic-chart-empty">
                    <div className="stat-group-heading">
                        <FormattedMessage id="analysis.stats.conversationTopics" defaultMessage="Conversation Topics" />
                    </div>
                    {coverageSubtitle}
                    <StatEmpty
                        periodSessionCount={periodSessionCount}
                        totalSessions={totalSessions}
                        isLoading={isLoading}
                        fallback={<FormattedMessage id="analysis.stats.noTopics" defaultMessage="No topic data available" />}
                    />
                </div>
            )}
        </div>
    </div>
}

export {isBlocked}
